/**
 * Wrapper around axios service.
 */
import axios, {AxiosRequestConfig} from 'axios';

import AuthActions from 'actions/AuthActions';
import camelize from 'camelize';
import snakeize from 'snakeize';
import qs from 'qs';

const DEFAULTS = {
  baseURL: '/api/v1',
  snakeizeRequestBody: true,
  snakeizeUrlParams: false,
  camelizeResponse: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Ev-Device': 'browser',
  },
};

const authInstances: Api[] = [];

const api4xxWhitelist: string[] = [];
const browserUrl4xxWhitelist: string[] = ['/externalCallShared'];

/**
 * new Api([config]) => Object to interact with the api
 */
export class Api {
  config = DEFAULTS;
  all = axios.all;
  spread = axios.spread;
  interceptors = {
    request: {
      use: (req, err) => this.instance.interceptors.request.use(req, err),
      eject: interceptor => this.instance.interceptors.request.eject(interceptor),
    },
    response: {
      use: (res, err) => this.instance.interceptors.response.use(res, err),
      eject: interceptor => this.instance.interceptors.response.eject(interceptor),
    },
  };
  accessDeniedInterceptor = null;
  instance: any = null;

  constructor(config = {}) {
    authInstances.push(this);

    this.config = {
      ...DEFAULTS,
      ...config,
    };

    /**
     * Make shortcut methods to the axios instance, axios helpers,
     * Interceptors request and response objects
     */
    const writeMethods = ['post', 'put', 'patch'];
    for (const key of ['delete', 'get', 'head'].concat(writeMethods)) {
      this[key] = function (this: any) {
        return this.instance[key].apply(this.instance, arguments);
      };
      this[key + 'Json'] = function (this: any) {
        const args = [...arguments];
        if (writeMethods.includes(key)) {
          if (this.config.snakeizeRequestBody) {
            args[1] = snakeize(args[1]);
          }
          if (this.config.snakeizeUrlParams && args[2] && args[2].params) {
            args[2].params = snakeize(args[2].params);
          }
        } else if (this.config.snakeizeUrlParams && args[1] && args[1].params) {
          args[1].params = snakeize(args[1].params);
        }
        return this.instance[key]
          .apply(this.instance, args)
          .then(({data}) => (this.config.camelizeResponse ? camelize(data) : data));
      };
    }

    this.reset();
  }

  static axiosCancelToken() {
    return axios.CancelToken.source();
  }

  static isCancel(thrown) {
    return axios.isCancel(thrown);
  }

  /**
   * Use brand new axios instance. Handy to remove all interceptors
   */
  reset() {
    this.instance = axios.create(this.config);
    this.setDefaultInterceptors();
  }

  /** Re-initialize `this.instance` so that future `api` related requests will be made with correct credentials */
  reinitialize() {
    this.instance = axios.create(this.config);
    this.setDefaultInterceptors();
  }

  /**
   * Set default interceptors
   */
  setDefaultInterceptors() {
    this.accessDeniedInterceptor = this.interceptors.response.use(
      response => response,
      error => {
        if (axios.isCancel(error)) {
          throw error;
        }
        const response = error.response;
        if (response && [401, 403].includes(response.status)) {
          // check a whitelist of possibilities before explosively redirecting to logout
          if (
            api4xxWhitelist.every(w => error.config.url !== w) &&
            browserUrl4xxWhitelist.every(w => !window.location.pathname.startsWith(w))
          ) {
            // Java APIs care about the authUser cookie, and will return 401 when you don't provide one
            // however, they don't send data.errors, so default to a user-friendly error message...
            let errorMessage = 'Your session has expired.';
            if (response.data.errors) {
              // ...but allow for a more specific error message from the python APIs if provided
              // this is used primarily for the multiple browser message
              const errorInfo = response.data.errors.filter(e => {
                return e.location === 'info' && e.name === 'showMessage';
              });
              errorMessage = errorInfo.length > 0 ? errorInfo[0].description : '';
            }
            if (!window.location.pathname.match(/\/login/i)) {
              AuthActions.logout({
                nextPathname: '/loggedOutWindow',
                message: errorMessage,
              });
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  ejectAccessDeniedInterceptor() {
    if (this.accessDeniedInterceptor !== null) {
      this.interceptors.response.eject(this.accessDeniedInterceptor);
    }
  }
}

export const apiV2 = new Api({baseURL: '/apiv2'}) as unknown as ApiInterface;

export const javaApiFor = baseUrl =>
  new Api({
    baseURL: baseUrl,
    snakeizeRequestBody: false,
    snakeizeUrlParams: true,
    camelizeResponse: false,
    paramsSerializer: function (params) {
      return qs.stringify(params, {arrayFormat: 'repeat'});
    },
  }) as unknown as ApiInterface;

export const dashboardApi = javaApiFor('/api/dashboard');
export const coachingApi = javaApiFor('/api/coaching');
export const callsApi = javaApiFor('/api/internal');
export const triggersApi = javaApiFor('/api/rules');
export const crmApi = javaApiFor('/api/crm');
export const assetsApi = javaApiFor('/api/assets');
export const adminApi = javaApiFor('/api/admin');
export const oauthApi = javaApiFor('/api/oauth');
export const profile = javaApiFor('/api/profile');
export const loginApi = javaApiFor('/api/login');

export default new Api() as unknown as ApiInterface;

interface ApiInterface {
  get: typeof axios.get;
  post: typeof axios.post;
  put: typeof axios.put;
  delete: typeof axios.delete;
  getJson: <Payload = any>(...args: Parameters<typeof axios.get>) => Promise<Payload>;
  postJson: <Params = any, Payload = any>(
    url: string,
    body: Params,
    config?: AxiosRequestConfig
  ) => Promise<Payload>;
  putJson: <Params = any, Payload = any>(
    url: string,
    body: Params,
    config?: AxiosRequestConfig
  ) => Promise<Payload>;
}
